import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { appToast } from './AppToast'

export default function AppExcelUploadView({
  title = 'Upload Excel Data',
  subtitle = 'Bulk import records using spreadsheets in 3 simple steps.',
  description = 'Use this option to upload records using Excel sheets. Download a sample template, fill in the details, and upload the file.',
  sampleFileName = 'Import_Data.xlsx',
  sampleFileUrl = '#',
  onDownloadSample,
  onUpload,
  cancelPath = '/lectures/list',
  backLabel = 'Back to List',
  masterRequirements = [],
}) {
  const navigate = useNavigate()
  const [file, setFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  const handleFile = (selectedFile) => {
    if (!selectedFile) return
    const name = selectedFile.name.toLowerCase()
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls') && !name.endsWith('.csv')) {
      appToast.error('Please upload a valid Excel (.xlsx, .xls) or CSV (.csv) file.')
      return
    }
    setFile(selectedFile)
  }

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    handleFile(f)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    const f = e.dataTransfer.files?.[0]
    handleFile(f)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleDownload = (e) => {
    if (onDownloadSample) {
      e.preventDefault()
      onDownloadSample()
    } else {
      appToast.info(`Downloading sample file: ${sampleFileName}...`)
    }
  }

  const handleUploadSubmit = async () => {
    if (!file) {
      appToast.error('Please select an Excel file before uploading.')
      return
    }
    setIsUploading(true)
    try {
      if (onUpload) {
        await onUpload(file)
      } else {
        await new Promise((r) => setTimeout(r, 1200))
        appToast.success(`File "${file.name}" imported successfully!`)
        if (cancelPath) navigate(cancelPath)
      }
    } catch (err) {
      appToast.error(err?.message || 'Failed to import Excel file.')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-20">
      {/* ── Top Header ── */}
      {/* <section className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant">
            {cancelPath && (
              <>
                <Link to={cancelPath} className="hover:text-primary transition">
                  {backLabel}
                </Link>
                <span>/</span>
              </>
            )}
            <span className="text-primary">{title}</span>
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {title}
          </h1>
          <p className="text-xs text-on-surface-variant">{subtitle}</p>
        </div>

        {cancelPath && (
          <Link
            to={cancelPath}
            className="self-start sm:self-auto flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            <span>{backLabel}</span>
          </Link>
        )}
      </section> */}

      {/* ── Main Upload Card ── */}
      <div className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs overflow-hidden">

        {/* Banner */}
        <div className="bg-primary px-8 py-5 text-on-primary">
          <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
          <p className="mt-0.5 text-xs text-on-primary/85 leading-relaxed">
            {description}
          </p>
        </div>

        {/* 3 Simple Steps */}
        <div className="p-8 flex flex-col gap-8">

          {/* Step 1 */}
          <div className="flex items-start gap-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary font-black text-sm">
              1
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-on-surface">Download Sample Template</h3>
              <p className="mt-0.5 text-xs text-on-surface-variant">
                Get the pre-formatted spreadsheet template with the exact columns and validation guidelines.
              </p>
              <div className="mt-3">
                <a
                  href={sampleFileUrl}
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2 text-xs font-bold text-primary hover:bg-primary/15 transition shadow-2xs"
                >
                  <span className="material-symbols-outlined text-base text-emerald-600">table_chart</span>
                  <span>{sampleFileName}</span>
                  <span className="material-symbols-outlined text-sm">download</span>
                </a>
              </div>
            </div>
          </div>

          <hr className="border-outline-variant/15" />

          {/* Step 2 */}
          <div className="flex items-start gap-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary font-black text-sm">
              2
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-on-surface">Enter &amp; Prepare Data</h3>
              <p className="mt-0.5 text-xs text-on-surface-variant leading-relaxed">
                Copy or fill your data into the sample template. Ensure that required master values match exactly.
              </p>
              {masterRequirements.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-bold text-on-surface-variant uppercase tracking-wider">Required Masters:</span>
                  {masterRequirements.map((req, idx) => (
                    <span
                      key={idx}
                      className="rounded-lg bg-surface-container-high px-2 py-0.5 text-[11px] font-semibold text-on-surface"
                    >
                      {req}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <hr className="border-outline-variant/15" />

          {/* Step 3 */}
          <div className="flex items-start gap-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary font-black text-sm">
              3
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-on-surface">Upload Excel File</h3>
              <p className="mt-0.5 text-xs text-on-surface-variant">
                Select your completed spreadsheet file (.xlsx, .xls, or .csv) to begin processing.
              </p>

              {/* Drag & Drop Upload Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`mt-4 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 text-center transition ${isDragOver
                    ? 'border-primary bg-primary/5'
                    : file
                      ? 'border-emerald-500/40 bg-emerald-500/5'
                      : 'border-outline-variant/40 bg-surface-container-low/50 hover:bg-surface-container-low'
                  }`}
              >
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <span className="material-symbols-outlined text-4xl text-emerald-600">check_circle</span>
                    <div className="text-xs">
                      <p className="font-bold text-on-surface">{file.name}</p>
                      <p className="text-[11px] text-on-surface-variant">
                        {(file.size / 1024).toFixed(1)} KB • Ready to Import
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="mt-2 text-xs font-bold text-error hover:underline cursor-pointer"
                    >
                      Remove &amp; select another file
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <span className="material-symbols-outlined text-4xl text-outline">cloud_upload</span>
                    <p className="text-xs font-bold text-on-surface">
                      Drag and drop your spreadsheet here, or browse
                    </p>
                    <p className="text-[11px] text-on-surface-variant">Supports .xlsx, .xls, and .csv</p>
                    <label className="mt-3 cursor-pointer rounded-xl bg-surface-container-highest px-4 py-2 text-xs font-bold text-on-surface hover:bg-outline-variant/40 transition shadow-2xs">
                      <span>Choose File</span>
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Footer Bar */}
        <div className="border-t border-outline-variant/20 bg-surface-container-low/60 px-8 py-4 flex items-center justify-end gap-3">
          {cancelPath && (
            <Link
              to={cancelPath}
              className="rounded-xl border border-outline-variant/30 bg-surface-container-high px-5 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-highest transition"
            >
              Cancel
            </Link>
          )}

          <button
            type="button"
            disabled={!file || isUploading}
            onClick={handleUploadSubmit}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base">upload</span>
                <span>Upload</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  )
}
